import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, ArrowRight, CheckCircle2, Briefcase, Zap, Heart, Globe, Cpu, Users, Award, Coffee, Code2, MapPin, Clock } from 'lucide-react';
import Seo from '../components/Seo.jsx';
import Container from '../components/Container.jsx';
import arif from '../assets/arif.jpg';
import shrishanth from '../assets/shrishanth.webp';
import rupika from '../assets/rupika.jpg';

// Import team showcase photos for Life at Spectranox block
import team1 from '../assets/Team-8.png';
import team2 from '../assets/Team-2.jpg';
import team3 from '../assets/Team-7.png';
import team4 from '../assets/Team-4.webp';
import { fetchWithFallback } from '../lib/contentFallback.js'

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.1 } }
};

export default function Careers() {
  const [activeFaq, setActiveFaq] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const API_BASE = import.meta.env.VITE_API_BASE || '/api';
    fetchWithFallback(
      `${API_BASE}/get-internships.php`,
      (d) => (d?.success && Array.isArray(d.internships) ? d.internships : null),
      'internships'
    )
      .then(({ data: internships }) => ({ success: true, internships }))
      .then(data => {
        if (data.success && data.internships) {
          const mapped = data.internships.map(job => ({
            id: job.id,
            slug: job.slug,
            title: job.title,
            dept: job.department,
            type: job.type || 'Full-time',
            exp: job.experience,
            loc: job.location
          }));
          setJobs(mapped);
        }
        setLoading(false);
      })
      .catch(err => {
        console.error("Failed to load internships:", err);
        setLoading(false);
      });
  }, []);

  const faqs = [
    { q: 'How do I apply?', a: 'You can apply directly through our Open Positions section by submitting your updated resume and portfolio.' },
    { q: 'What is the interview process?', a: 'Our process includes an initial HR screening, a technical assessment or portfolio review, and a final discussion with the leadership team.' },
    { q: 'Do you offer remote opportunities?', a: 'Yes, we offer fully remote and hybrid roles depending on the specific position and team requirements.' },
    { q: 'What technologies do you work with?', a: 'We specialize in React, Node.js, Next.js, Python, AWS, Azure, and various modern AI/ML frameworks.' },
    { q: 'Do freshers have opportunities?', a: 'Absolutely! We regularly hire passionate freshers for junior roles and internship programs.' },
    { q: 'How long does the hiring process take?', a: 'We move fast. Typically, the entire process from application to offer takes between 1 to 2 weeks.' }
  ];

  return (
    <div className="min-h-screen bg-[#F8FAFC] text-slate-900 font-sans selection:bg-blue-500/30 pt-20">
      <Seo title="Careers" description="Open roles at Spectranox: engineering, design, AI and security. Join a founder-led team building software for clients across ten countries." path="/careers" />

      {/* =========================================
          1. PREMIUM HERO SECTION (Image Replica)
      ========================================= */}
      <section className="relative overflow-hidden bg-[#0A0C10] w-full pt-10 lg:pt-16 pb-0 lg:pb-24">
        {/* Abstract Glowing Backgrounds */}
        <div className="absolute top-[-20%] left-[-10%] w-[50%] h-[50%] rounded-full bg-emerald-500/10 blur-[120px] pointer-events-none" />
        <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] rounded-full bg-blue-600/10 blur-[120px] pointer-events-none" />

        <div className="relative z-10 mx-auto w-full max-w-7xl px-6 lg:px-10 h-full flex items-center">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-8 items-center w-full max-h-full">
            
            {/* Left: Text Content */}
            <motion.div initial="hidden" animate="visible" variants={staggerContainer} className="max-w-xl text-white py-4">
              
              {/* Badge */}
              <motion.div variants={fadeUp} className="inline-flex items-center gap-3 rounded-full bg-[#121A20] border border-emerald-500/20 px-1 py-1 pr-1.5 mb-6">
                <div className="flex items-center gap-2 px-3 py-1">
                  <span className="h-1.5 w-1.5 rounded-full bg-emerald-400"></span>
                  <span className="text-[10px] font-bold tracking-widest text-emerald-400">WE'RE HIRING</span>
                </div>
              </motion.div>
              
              <motion.h1 variants={fadeUp} className="font-display text-4xl sm:text-5xl lg:text-[4.2rem] font-bold leading-[1.05] tracking-tight mb-5">
                Build <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-200 to-emerald-400">what</span> you love.
              </motion.h1>
              
              <motion.p variants={fadeUp} className="text-sm sm:text-base text-slate-300 leading-relaxed mb-8 max-w-lg">
                We're a small, senior team building production software for clients in <strong className="text-white font-semibold">10 countries</strong>. Websites, apps, AI systems, connected hardware and security work, most of it live inside <strong className="text-white font-semibold">45 days</strong>. You own what you build here, from the architecture call through to the release.
              </motion.p>
              
              <motion.div variants={fadeUp} className="flex flex-wrap items-center gap-3 mb-10">
                <a href="#open-positions" className="btn-on-solid inline-flex items-center justify-center gap-2 rounded-full bg-emerald-700 px-5 py-3 text-sm font-bold text-white transition-all hover:bg-emerald-600 hover:scale-105">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M4 14.899A7 7 0 1 1 15.71 8h1.79a4.5 4.5 0 0 1 2.5 8.242"></path><path d="M12 12v9"></path><path d="m8 16 4-4 4 4"></path></svg>
                  See open roles
                  <ArrowRight size={16} strokeWidth={3} />
                </a>
                <a href="#contact" className="inline-flex items-center justify-center gap-2 rounded-full bg-[#1A1F26] border border-slate-700/50 px-5 py-3 text-sm font-bold text-white transition-all hover:bg-[#252B33]">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M14 9a2 2 0 0 1-2 2H6l-4 4V4c0-1.1.9-2 2-2h8a2 2 0 0 1 2 2z"></path><path d="M18 9h2a2 2 0 0 1 2 2v11l-4-4h-6a2 2 0 0 1-2-2v-1"></path></svg>
                  Talk to a hiring lead
                </a>
              </motion.div>

              {/* Stats Row */}
              <motion.div variants={fadeUp} className="flex flex-wrap items-center gap-y-4 gap-x-6 lg:gap-x-8 border-l-[3px] border-emerald-400 pl-4">
                <div>
                  <div className="text-xl sm:text-2xl font-bold text-white leading-none mb-1">45</div>
                  <div className="text-[8px] font-bold text-emerald-400 uppercase tracking-widest">DAYS · TYPICAL BUILD</div>
                </div>
                <div className="w-px h-6 bg-slate-700/50 hidden sm:block"></div>
                <div>
                  <div className="text-xl sm:text-2xl font-bold text-white leading-none mb-1">10</div>
                  <div className="text-[8px] font-bold text-emerald-400 uppercase tracking-widest">COUNTRIES SERVED</div>
                </div>
                <div className="w-px h-6 bg-slate-700/50 hidden lg:block"></div>
                <div>
                  <div className="text-xl sm:text-2xl font-bold text-white leading-none mb-1">100%</div>
                  <div className="text-[8px] font-bold text-emerald-400 uppercase tracking-widest">REMOTE-FRIENDLY</div>
                </div>
                <div className="w-px h-6 bg-slate-700/50 hidden sm:block"></div>
                <div>
                  <div className="text-xl sm:text-2xl font-bold text-white leading-none mb-1">6+</div>
                  <div className="text-[8px] font-bold text-emerald-400 uppercase tracking-widest">YEARS SHIPPING</div>
                </div>
              </motion.div>
            </motion.div>

            {/* Right: Floating Polaroids & UI Widgets */}
            {/* On a phone this cluster used to sit inside the page gutter and
                then shrink itself another 10%, so two 150px polaroids floated
                in the middle of the screen with a wide empty margin either
                side. Below sm it now cancels the gutter and runs edge to edge,
                at full scale, and the cards grow with the viewport. */}
            <div className="relative -mx-6 w-[calc(100%+3rem)] h-[430px] sm:mx-0 sm:h-[550px] sm:w-full lg:h-[600px] mt-4 lg:mt-0 perspective-1000 origin-center sm:scale-100 lg:origin-right">
              
              {/* Central dashed circle decorative */}
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[min(78vw,340px)] h-[min(78vw,340px)] rounded-full border border-dashed border-slate-700/50 opacity-50 pointer-events-none hidden sm:block" />

              {/* Polaroid 1 (Arif) */}
              <motion.div 
                initial={{ opacity: 0, y: 20, rotate: 0 }}
                animate={{ opacity: 1, y: 0, rotate: -6 }}
                transition={{ duration: 0.8, delay: 0.2 }}
                className="absolute left-[6%] top-[4%] z-20 w-[42vw] max-w-[190px] sm:w-[180px] rounded-sm bg-[#FDFBF7] p-2 pb-6 shadow-2xl origin-bottom-left"
              >
                <div className="absolute -top-3 right-4 bg-emerald-400 text-[#0A0C10] text-[8px] font-bold px-2 py-0.5 rounded-sm transform rotate-3 z-10 shadow-sm">Developer</div>
                <img src={arif} className="w-full aspect-[4/5] object-cover rounded-sm border border-slate-200" />
                <p className="absolute bottom-1 inset-x-0 text-center font-display text-base font-bold text-[#4A3B32]">Arif</p>
              </motion.div>

              {/* Polaroid 2 (Shreeshanth) */}
              <motion.div 
                initial={{ opacity: 0, y: 20, rotate: 0 }}
                animate={{ opacity: 1, y: 0, rotate: 8 }}
                transition={{ duration: 0.8, delay: 0.4 }}
                className="absolute right-[6%] top-[27%] z-10 w-[42vw] max-w-[190px] sm:w-[180px] rounded-sm bg-[#FDFBF7] p-2 pb-6 shadow-2xl origin-top-right"
              >
                <div className="absolute -top-3 left-4 bg-indigo-400 text-white text-[8px] font-bold px-2 py-0.5 rounded-sm transform -rotate-3 z-10 shadow-sm">Cyber Security Analyst</div>
                <img src={shrishanth} className="w-full aspect-[4/5] object-cover rounded-sm border border-slate-200" />
                <p className="absolute bottom-1 inset-x-0 text-center font-display text-base font-bold text-[#4A3B32]">Shrishanth</p>
              </motion.div>

              {/* Polaroid 3 (Rupika) */}
              <motion.div 
                initial={{ opacity: 0, y: 20, rotate: 0 }}
                animate={{ opacity: 1, y: 0, rotate: -4 }}
                transition={{ duration: 0.8, delay: 0.6 }}
                className="absolute left-[15%] bottom-[5%] z-30 w-[42vw] max-w-[190px] sm:w-[180px] rounded-sm bg-[#FDFBF7] p-2 pb-6 shadow-2xl origin-top-left hidden md:block"
              >
                <div className="absolute -top-3 right-4 bg-[#F5C77E] text-[#0A0C10] text-[8px] font-bold px-2 py-0.5 rounded-sm transform rotate-2 z-10 shadow-sm">Security Engineer</div>
                <img src={rupika} className="w-full aspect-[4/5] object-cover rounded-sm border border-slate-200" />
                <p className="absolute bottom-1 inset-x-0 text-center font-display text-base font-bold text-[#4A3B32]">Rupika</p>
              </motion.div>

              {/* Floating Chat Widget */}
              <motion.div 
                animate={{ y: [-5, 5, -5] }} transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
                className="absolute top-[8%] -right-[5%] lg:-right-[5%] z-40 bg-[#141A23] border border-slate-800 rounded-xl p-3 shadow-2xl w-[200px] hidden md:block"
              >
                <div className="flex items-center gap-2 mb-2">
                  <span className="flex items-center gap-1 bg-emerald-500/20 text-emerald-400 text-[7px] font-bold px-1.5 py-0.5 rounded-sm uppercase tracking-wide"><span className="w-1.5 h-1.5 bg-emerald-400 rounded-full animate-pulse"></span> LIVE - MAIN</span>
                  <span className="text-[8px] text-slate-500 font-mono tracking-wide">chat reactions</span>
                </div>
                <div className="space-y-1 font-mono text-[8px]">
                  <div className="flex gap-2 text-slate-300"><span className="text-purple-400 font-bold min-w-[25px]">arjun</span> fixed login race</div>
                  <div className="flex gap-2 text-slate-300"><span className="text-amber-400 font-bold min-w-[25px]">divya</span> merged #482 search</div>
                  <div className="flex gap-2 text-slate-300"><span className="text-cyan-400 font-bold min-w-[25px]">rohit</span> deployed v2.18</div>
                  <div className="flex gap-2 text-slate-300 items-start"><span className="text-rose-400 font-bold min-w-[25px]">hema</span> <span className="leading-tight">opened RFC: cohort<br/>billing</span></div>
                </div>
              </motion.div>

              {/* Floating Chart Widget */}
              <motion.div 
                animate={{ y: [5, -5, 5] }} transition={{ duration: 5, repeat: Infinity, ease: "easeInOut", delay: 1 }}
                className="absolute bottom-[10%] -right-[5%] lg:-right-[5%] z-40 bg-[#1A222C] border border-slate-700/50 rounded-2xl p-3 shadow-2xl w-[200px] hidden md:block"
              >
                <div className="flex justify-between items-center mb-3">
                  <span className="text-[8px] font-bold text-slate-400 uppercase tracking-widest">TEAM GROWTH</span>
                  <span className="text-[9px] font-bold text-emerald-400 flex items-center gap-1"><svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><path d="m5 12 7-7 7 7"/><path d="M12 19V5"/></svg>+172%</span>
                </div>
                <div className="flex items-end gap-1 h-12 mb-2">
                  {[20, 30, 45, 60, 75, 100].map((h, i) => (
                    <div key={i} className="w-full bg-blue-500/20 rounded-t-sm relative group overflow-hidden" style={{ height: `${h}%` }}>
                      {i === 5 ? (
                        <div className="absolute inset-0 bg-gradient-to-t from-emerald-500 to-emerald-300 rounded-t-sm shadow-[0_0_10px_rgba(52,211,153,0.5)]"></div>
                      ) : (
                        <div className="absolute inset-0 bg-blue-500/50 rounded-t-sm"></div>
                      )}
                    </div>
                  ))}
                </div>
                <div className="flex justify-between text-[7px] font-mono text-slate-500">
                  <span>'22</span><span>'23</span><span>'24</span><span>'25</span><span>'26</span>
                </div>
                <div className="mt-2 flex items-center justify-between border-t border-slate-700/50 pt-2">
                  <span className="text-lg font-bold text-white">60+</span>
                  <span className="text-[8px] text-slate-400">teammates today</span>
                </div>
              </motion.div>

            </div>
          </div>
        </div>
      </section>

      {/* =========================================
          2. WHY JOIN SPECTRONX
      ========================================= */}
      <section className="py-24 bg-white relative">
        <Container>
          <div className="text-center max-w-2xl mx-auto mb-16">
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-slate-900 mb-4">Why Join Spectranox</h2>
            <p className="text-slate-500 text-lg">We provide an environment where your skills are nurtured, your ideas are valued, and your career grows exponentially.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: <Zap />, title: 'Innovative Projects', desc: 'Work on cutting-edge technologies and shape the future of digital solutions.' },
              { icon: <Globe />, title: 'Flexible Work', desc: 'Enjoy remote or hybrid options tailored to help you do your best work.' },
              { icon: <Award />, title: 'Career Growth', desc: 'Clear progression paths, mentorship, and opportunities to lead.' },
              { icon: <Users />, title: 'Friendly Culture', desc: 'A collaborative, inclusive environment where every voice matters.' }
            ].map((feature, idx) => (
              <motion.div 
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                className="group relative rounded-3xl bg-slate-50 p-8 hover:bg-white border border-transparent hover:border-slate-200 shadow-sm hover:shadow-xl transition-all duration-300"
              >
                <div className="absolute inset-0 rounded-3xl bg-gradient-to-b from-blue-50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                <div className="relative z-10">
                  <div className="w-12 h-12 rounded-2xl bg-white shadow-sm flex items-center justify-center text-blue-600 mb-6 group-hover:scale-110 group-hover:text-cyan-500 transition-all">
                    {feature.icon}
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 mb-3">{feature.title}</h3>
                  <p className="text-slate-600 text-sm leading-relaxed">{feature.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </Container>
      </section>

      {/* =========================================
          3. LIFE AT SPECTRONX (Masonry & List)
      ========================================= */}
      <section className="py-24 bg-slate-50 border-t border-slate-200/60 overflow-hidden">
        <Container>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            
            {/* Left: Masonry Photos */}
            <div className="grid grid-cols-2 gap-4">
              <div className="flex flex-col gap-4 pt-12">
                <img src={team1} alt="Team 1" className="rounded-2xl shadow-lg w-full h-[280px] object-cover hover:scale-[1.02] transition-transform" />
                <img src={team2} alt="Team 2" className="rounded-2xl shadow-lg w-full h-[200px] object-cover hover:scale-[1.02] transition-transform" />
              </div>
              <div className="flex flex-col gap-4">
                <img src={team3} alt="Team 3" className="rounded-2xl shadow-lg w-full h-[200px] object-cover hover:scale-[1.02] transition-transform" />
                <img src={team4} alt="Team 4" className="rounded-2xl shadow-lg w-full h-[280px] object-cover hover:scale-[1.02] transition-transform" />
              </div>
            </div>

            {/* Right: Culture Points */}
            <div>
              <h2 className="font-display text-3xl sm:text-4xl font-bold text-slate-900 mb-6">Life at Spectranox</h2>
              <p className="text-slate-600 text-lg mb-10 leading-relaxed">
                We're more than just colleagues. We're a community of driven individuals who believe in working hard, learning constantly, and celebrating our wins together.
              </p>

              <div className="space-y-6">
                {[
                  { title: "Continuous Learning", desc: "Weekly knowledge sharing sessions, sponsored courses, and a dedicated learning budget for every team member." },
                  { title: "Epic Retreats", desc: "Annual company-wide getaways to beautiful locations where we disconnect, bond, and recharge." },
                  { title: "Modern Workspace", desc: "Ergonomic setups, high-end gear, and spaces designed for deep focus and spontaneous collaboration." }
                ].map((item, i) => (
                  <div key={i} className="flex gap-4 p-4 rounded-2xl hover:bg-white hover:shadow-md transition-all border border-transparent hover:border-slate-200">
                    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center font-bold text-sm">
                      {i + 1}
                    </div>
                    <div>
                      <h4 className="text-lg font-bold text-slate-900 mb-1">{item.title}</h4>
                      <p className="text-slate-600 text-sm">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
          </div>
        </Container>
      </section>

      {/* =========================================
          4. OPEN POSITIONS
      ========================================= */}
      {!loading && jobs.length > 0 && (
        <section id="open-positions" className="py-24 bg-white">
          <Container>
            <div className="flex flex-col md:flex-row md:items-end justify-between mb-16 gap-6">
              <div className="max-w-2xl">
                <h2 className="font-display text-3xl sm:text-4xl font-bold text-slate-900 mb-4">Open Positions</h2>
                <p className="text-slate-500 text-lg">Don't see your perfect role? Send us your resume anyway. We're always looking for great talent.</p>
              </div>
              <a
                href="#contact"
                className="flex-shrink-0 px-6 py-2.5 rounded-full border border-slate-300 text-slate-700 font-medium hover:bg-slate-50 transition-colors"
              >
                Send an open application
              </a>
            </div>

            {!loading && jobs.length === 0 && (
              <div className="rounded-3xl border border-dashed border-slate-300 py-16 text-center">
                <p className="text-sm font-semibold text-slate-700">No roles open right now</p>
                <p className="mx-auto mt-2 max-w-sm text-sm text-slate-500">
                  Send us your resume anyway. We keep good applications on file and come back to them.
                </p>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {jobs.map((job, i) => (
                <motion.div 
                  key={job.id || i}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                >
                  <Link
                    to={`/careers/${job.slug || job.id}`}
                    className="group p-8 rounded-3xl border border-slate-200 bg-white hover:border-emerald-600 hover:shadow-[0_8px_30px_rgba(4,120,87,0.14)] transition-all duration-300 flex flex-col h-full block cursor-pointer"
                  >
                    <div className="flex justify-between items-start mb-6">
                      <div>
                        <span className="inline-block px-3 py-1 rounded-full bg-slate-100 text-slate-600 text-xs font-semibold uppercase tracking-wider mb-3">
                          {job.dept}
                        </span>
                        <h3 className="text-2xl font-bold text-slate-900 group-hover:text-emerald-700 transition-colors">{job.title}</h3>
                      </div>
                      <div className="w-10 h-10 rounded-full bg-slate-50 flex items-center justify-center text-slate-400 group-hover:bg-emerald-700 group-hover:text-white transition-colors transform group-hover:-rotate-45">
                        <ArrowRight size={20} />
                      </div>
                    </div>
                    
                    <div className="mt-auto flex flex-wrap gap-4 text-sm text-slate-500 font-medium">
                      <div className="flex items-center gap-1.5"><Briefcase size={16} className="text-slate-400" /> {job.type}</div>
                      <div className="flex items-center gap-1.5"><MapPin size={16} className="text-slate-400" /> {job.loc}</div>
                      <div className="flex items-center gap-1.5"><Clock size={16} className="text-slate-400" /> {job.exp}</div>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>
          </Container>
        </section>
      )}

      {/* =========================================
          5. HIRING PROCESS (Timeline)
      ========================================= */}
      <section className="py-24 bg-slate-900 text-white relative overflow-hidden">
        {/* Glow */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[300px] bg-blue-600/20 blur-[150px] rounded-full pointer-events-none" />

        <Container className="relative z-10">
          <div className="text-center max-w-2xl mx-auto mb-20">
            <h2 className="font-display text-3xl sm:text-4xl font-bold mb-4">Our Hiring Process</h2>
            <p className="text-slate-400 text-lg">Fast, transparent, and designed to help you showcase your best work.</p>
          </div>

          <div className="relative">
            {/* Connecting Line (Desktop) */}
            <div className="hidden md:block absolute top-[45px] left-[10%] right-[10%] h-0.5 bg-slate-800">
              <div className="absolute top-0 left-0 h-full bg-gradient-to-r from-blue-500 to-cyan-400 w-1/2 opacity-50" />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-12 md:gap-6 relative">
              {[
                { step: '01', title: 'Application', desc: 'Submit your resume and portfolio. We review every application.' },
                { step: '02', title: 'Screening Call', desc: 'A quick chat with HR to understand your goals and culture fit.' },
                { step: '03', title: 'Technical Interview', desc: 'Deep dive into your skills with our engineering or design leads.' },
                { step: '04', title: 'Offer & Onboarding', desc: 'Welcome to the team! We make the transition smooth.' }
              ].map((process, i) => (
                <div key={i} className="relative flex flex-col items-center text-center">
                  <div className="w-24 h-24 rounded-full bg-slate-800 border-4 border-slate-900 shadow-xl flex items-center justify-center text-2xl font-display font-bold text-transparent bg-clip-text bg-gradient-to-br from-blue-400 to-cyan-300 mb-6 relative z-10">
                    {process.step}
                  </div>
                  <h3 className="text-xl font-bold mb-3">{process.title}</h3>
                  <p className="text-sm text-slate-400 leading-relaxed max-w-[200px]">{process.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </Container>
      </section>

      {/* =========================================
          6. EMPLOYEE BENEFITS
      ========================================= */}
      <section id="benefits" className="py-24 bg-[#F8FAFC]">
        <Container>
          <div className="text-center max-w-2xl mx-auto mb-16">
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-slate-900 mb-4">Employee Benefits</h2>
            <p className="text-slate-500 text-lg">We take care of our team so they can focus on doing their best work.</p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6">
            {[
              { icon: <Heart />, label: 'Health Insurance' },
              { icon: <Coffee />, label: 'Free Snacks & Coffee' },
              { icon: <Globe />, label: 'Remote Options' },
              { icon: <Zap />, label: 'Performance Bonus' },
              { icon: <CheckCircle2 />, label: 'Paid Time Off' },
              { icon: <Users />, label: 'Team Retreats' },
              { icon: <Award />, label: 'Learning Budget' },
              { icon: <Briefcase />, label: 'Latest Gear' }
            ].map((benefit, i) => (
              <div key={i} className="flex flex-col items-center justify-center text-center p-6 rounded-2xl bg-white border border-slate-200/60 shadow-sm hover:shadow-md transition-shadow">
                <div className="w-12 h-12 rounded-full bg-blue-50 text-blue-600 flex items-center justify-center mb-4">
                  {benefit.icon}
                </div>
                <h4 className="font-bold text-slate-800 text-sm">{benefit.label}</h4>
              </div>
            ))}
          </div>
        </Container>
      </section>

      {/* =========================================
          7. FAQ SECTION
      ========================================= */}
      <section className="py-24 bg-white border-t border-slate-100">
        <Container>
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="font-display text-3xl sm:text-4xl font-bold text-slate-900 mb-4">Frequently Asked Questions</h2>
            </div>
            
            <div className="space-y-4">
              {faqs.map((faq, i) => {
                const isOpen = activeFaq === i;
                return (
                  <div key={i} className="border border-slate-200 rounded-2xl overflow-hidden transition-colors hover:border-blue-200">
                    <button 
                      onClick={() => setActiveFaq(isOpen ? null : i)}
                      className="w-full flex items-center justify-between p-6 text-left bg-white focus:outline-none"
                    >
                      <span className="font-bold text-slate-900 pr-4">{faq.q}</span>
                      <ChevronDown className={`flex-shrink-0 text-slate-400 transition-transform duration-300 ${isOpen ? 'rotate-180 text-blue-600' : ''}`} />
                    </button>
                    <AnimatePresence>
                      {isOpen && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.3 }}
                        >
                          <div className="px-6 pb-6 text-slate-600 text-sm leading-relaxed border-t border-slate-100 pt-4">
                            {faq.a}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                )
              })}
            </div>
          </div>
        </Container>
      </section>

      {/* =========================================
          8. FINAL CTA
      ========================================= */}
      <section className="py-24 bg-[#F8FAFC]">
        <Container>
          <div className="relative overflow-hidden rounded-[2.5rem] bg-gradient-to-br from-blue-600 to-cyan-500 p-10 sm:p-16 text-center shadow-2xl">
            {/* Decorative background circles */}
            <div className="absolute top-0 right-0 -translate-y-1/2 translate-x-1/3 w-[600px] h-[600px] rounded-full bg-white/10 blur-3xl" />
            <div className="absolute bottom-0 left-0 translate-y-1/3 -translate-x-1/4 w-[400px] h-[400px] rounded-full bg-black/10 blur-2xl" />

            <div className="relative z-10 max-w-2xl mx-auto text-white">
              <h2 className="font-display text-4xl sm:text-5xl font-bold mb-6 tracking-tight">Ready to Grow Your Career with Spectranox?</h2>
              <p className="text-blue-100 text-lg mb-10 max-w-xl mx-auto">
                Become part of a team that's shaping the future of technology through innovation, creativity, and excellence.
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <button className="rounded-full bg-white px-8 py-3.5 text-sm font-bold text-blue-600 shadow-xl transition-transform hover:-translate-y-1 hover:shadow-2xl">
                  Apply Now
                </button>
                <button className="rounded-full bg-white/10 border border-white/20 backdrop-blur-md px-8 py-3.5 text-sm font-bold text-white transition-colors hover:bg-white/20">
                  Contact HR
                </button>
              </div>
            </div>
          </div>
        </Container>
      </section>

    </div>
  );
}
